import { useEffect, useState } from "react";

import AutoCompleteDropdown from "../../../../ui library/components/dropdowns/AutoCompleteDropdown";

import CustomListBox from "./CustomListBox";

const DEFAULT_LIMIT = 100;
const DEFAULT_OFFSET = 0;

export default function SearchableProviderDropdown({
  label,
  repository,
  mode,
  caseId,
  initValue = null,
  onChange,
  disabled = false,
  required = false,
  enableShowMore = false,
}) {
  const [selectedValue, setSelectedValue] = useState(initValue ?? null);
  const [data, setData] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [searchText, setSearchText] = useState("");

  const parseData = (items = []) =>
    items.map((item) => {
      if (mode === "relatedProviders") {
        return {
          id: item?.provider?.id ?? item?.provider?.pk ?? item?.key,
          key: item?.provider?.id ?? item?.provider?.pk ?? item?.key,
          label: item?.provider?.name ?? item?.value,
          providerTypes: item?.provider?.providerTypes ?? item?.providerTypes ?? [],
        };
      }

      return {
        id: item?.id ?? item?.key,
        key: item?.id ?? item?.key,
        label: item?.name ?? item?.value,
        providerTypes: item?.providerTypes ?? [],
      };
    });

  const fetchData = async (
    search = "",
    limit = DEFAULT_LIMIT,
    offset = DEFAULT_OFFSET
  ) => {
    const params = { limit, offset };

    if (search) {
      params.search = search;
    }

    const response =
      mode === "relatedProviders"
        ? await repository.getRelatedProviders(caseId, params)
        : await repository.getProviders(params);

    const mapped = parseData(response);

    if (offset === 0) {
      setData(mapped);
    } else {
      setData((prev) => [...prev, ...mapped]);
    }

    const next = await repository.getNextPage();
    setNextPage(next);
    setShowMore(Boolean(next));
  };

  const fetchNextPage = async () => {
    if (!nextPage) {
      return;
    }

    const url = new URL(nextPage);
    const limit = parseInt(url.searchParams.get("limit"), 10);
    const offset = parseInt(url.searchParams.get("offset"), 10);

    await fetchData(searchText, limit, offset);
  };

  const reset = async () => {
    setSelectedValue(null);
    setSearchText("");
    await fetchData("", DEFAULT_LIMIT, DEFAULT_OFFSET);
    onChange(null);
  };

  useEffect(() => {
    setSelectedValue(initValue ?? null);
  }, [initValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(searchText, DEFAULT_LIMIT, DEFAULT_OFFSET);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchText, caseId, mode]);

  const handleOnChange = async (value) => {
    if (!value) {
      await reset();
      return;
    }

    setSelectedValue(value);
    onChange(value);
  };

  return (
    <AutoCompleteDropdown
      freeSolo
      label={`${label}${required ? "*" : ""}`}
      data={data}
      initValue={selectedValue}
      disabled={disabled}
      addNoSelectionOption={false}
      getOptionLabel={(option) => (typeof option === "string" ? option : option?.label || "")}
      onValueChange={(_, value) => handleOnChange(value)}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          setSearchText(newInputValue);
        } else if (reason === "clear") {
          reset();
        }
      }}
      {...(enableShowMore
        ? {
            ListboxComponent: CustomListBox,
            ListboxProps: {
              showMore,
              setShowMore: fetchNextPage,
            },
          }
        : {})}
    />
  );
}
